// Context panel "Assistant" view — grounded Q&A over the caller's meetings, Robin-teal
// accent. REAL data: `useAssistantAsk()` → `POST /assistant/ask` (openapi `askAssistant`).
//
// Like Summary, this is deliberately NOT gated by `panelsEnabled()` — nothing here is
// fabricated. It IS behind its own runtime switch, `useFeatureEnabled('assistant')` (FEATURES-1,
// default off): off → the tab is hidden and this renders the old "Coming soon" state. Every
// answer comes from the backend, which only cites transcript segments
// it actually retrieved for this caller (permission-filtered server-side). The only
// static copy is the generic starter questions shown before the first ask.
//
// Scope: when a recording is open, the question defaults to that recording ("This
// recording"); the user can switch to "All meetings". Citations open the cited recording
// — the same destination as a global-search result (AppShell's `selectSearchResult`);
// the app has no URL-addressable seek yet, so the timestamp is shown in the label.
import { useEffect, useState, type FormEvent } from 'react';
import {
  SparklesIcon,
  DocumentTextIcon,
  ChatBubbleLeftIcon,
  ArrowUpIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { ScribeApiError } from '@scribe/core/client';
import type { AssistantCitation } from '@scribe/core/types';
import { useFeatureEnabled } from '../../lib/features';
import { useAssistantAsk } from '../../lib/queries';
import { ComingSoonPanel } from './ComingSoon';

const MAX_QUESTION_LENGTH = 2000;

const STARTER_QUESTIONS = [
  'What decisions were made?',
  'What are the open action items?',
];

type Scope = 'recording' | 'all';

function errorMessage(error: unknown): string {
  if (error instanceof ScribeApiError) {
    if (error.code === 'budget_exceeded') {
      return error.message || 'The monthly AI cost cap has been reached.';
    }
    if (error.status === 403) return "You don't have access to that recording.";
    if (error.status === 404) return 'That recording no longer exists.';
    if (error.code === 'assistant_timeout') {
      return 'That took too long — try a narrower question.';
    }
    if (error.status === 503) return "The assistant couldn't answer right now. Please try again.";
  }
  return 'Something went wrong asking the assistant. Please try again.';
}

interface AssistantProps {
  /** The recording currently open in the main view, if any. */
  recordingId?: string;
  /** Invoked when a citation is activated (App navigates to the cited recording). */
  onOpenCitation?: (citation: AssistantCitation) => void;
}

export function Assistant(props: AssistantProps) {
  const enabled = useFeatureEnabled('assistant');
  if (!enabled) {
    return (
      <ComingSoonPanel
        icon={<SparklesIcon width={20} height={20} />}
        title="Ask across all your meetings"
        description="An AI assistant that answers questions from your meeting history isn't available yet."
      />
    );
  }
  return <AssistantAsk {...props} />;
}

function AssistantAsk({ recordingId, onOpenCitation }: AssistantProps) {
  const [question, setQuestion] = useState('');
  const [scope, setScope] = useState<Scope>(recordingId ? 'recording' : 'all');
  const [asked, setAsked] = useState<string | null>(null);
  const ask = useAssistantAsk();

  // Opening a different recording (or closing it) resets the default scope.
  useEffect(() => {
    setScope(recordingId ? 'recording' : 'all');
  }, [recordingId]);

  const scopedId = scope === 'recording' ? recordingId : undefined;

  function send(raw: string) {
    const q = raw.trim().slice(0, MAX_QUESTION_LENGTH);
    if (!q || ask.isPending) return;
    setAsked(q);
    ask.mutate(scopedId ? { question: q, recording_id: scopedId } : { question: q });
    setQuestion('');
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    send(question);
  }

  const answer = ask.data;
  const suggestions = answer ? answer.suggestions : asked ? [] : STARTER_QUESTIONS;

  return (
    <div className="assistant-view">
      <div className="panel-section-title">
        {scopedId ? 'Ask about this recording' : 'Ask across all your meetings'}
      </div>

      {recordingId && (
        <div className="tags" role="group" aria-label="Question scope">
          <button
            type="button"
            className={'chip chip-toggle' + (scope === 'recording' ? ' chip-selected' : '')}
            aria-pressed={scope === 'recording'}
            onClick={() => setScope('recording')}
          >
            This recording
          </button>
          <button
            type="button"
            className={'chip chip-toggle' + (scope === 'all' ? ' chip-selected' : '')}
            aria-pressed={scope === 'all'}
            onClick={() => setScope('all')}
          >
            All meetings
          </button>
        </div>
      )}

      <div aria-live="polite" className="assistant-result">
        {asked && (
          <div className="ai-card">
            <p className="assistant-question">{asked}</p>
            <div className="ai-card-who">
              <span className="ai-card-badge" aria-hidden="true">
                <SparklesIcon width={14} height={14} />
              </span>
              Scribe assistant
            </div>

            {ask.isPending && (
              <p className="ai-card-text assistant-pending">
                <ArrowPathIcon width={14} height={14} className="spinner" aria-hidden="true" />
                Thinking…
              </p>
            )}

            {ask.isError && (
              <div className="assistant-error" role="alert">
                <p className="ai-card-text">{errorMessage(ask.error)}</p>
                <button type="button" className="sm" onClick={() => send(asked)}>
                  Try again
                </button>
              </div>
            )}

            {answer && !ask.isPending && (
              <>
                <p className="ai-card-text assistant-answer">{answer.answer}</p>
                {answer.citations.length > 0 && (
                  <div className="assistant-citations">
                    <div className="panel-section-title">Sources</div>
                    {answer.citations.map((c) => (
                      <button
                        type="button"
                        key={c.segment_id}
                        className="suggestion-task assistant-citation"
                        onClick={() => onOpenCitation?.(c)}
                        aria-label={`Open source: ${c.label}`}
                      >
                        <DocumentTextIcon width={16} height={16} aria-hidden="true" />
                        <span className="suggestion-text">{c.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="panel-section-count">Generated by AI from your transcripts · check the sources</div>
              </>
            )}
          </div>
        )}
      </div>

      {suggestions.length > 0 && !ask.isPending && (
        <>
          <div className="panel-section-title">{answer ? 'Follow-ups' : 'Try asking'}</div>
          <div className="assistant-suggestions">
            {suggestions.map((s) => (
              <button type="button" key={s} className="suggestion-task" onClick={() => send(s)}>
                <span className="suggestion-cbox" aria-hidden="true" />
                <span className="suggestion-text">{s}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <form className="assistant-ask" onSubmit={submit}>
        <ChatBubbleLeftIcon width={18} height={18} aria-hidden="true" />
        <input
          type="text"
          className="assistant-ask-input"
          value={question}
          maxLength={MAX_QUESTION_LENGTH}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={scopedId ? 'Ask about this recording…' : 'Ask anything about your conversations…'}
          aria-label={scopedId ? 'Ask about this recording' : 'Ask anything about your conversations'}
        />
        <button
          type="submit"
          className="icon-btn assistant-ask-submit"
          aria-label="Send question"
          disabled={!question.trim() || ask.isPending}
        >
          <ArrowUpIcon width={20} height={20} aria-hidden="true" />
        </button>
      </form>
    </div>
  );
}
